import * as Keychain from 'react-native-keychain';
import type { BiometryType } from './biometrics';

const FIRST_LOGIN_SECURITY_SERVICE = 'one-app-first-login-security';

export type FirstLoginSecurityStep = 'biometrics' | 'password' | 'completed';

const isFirstLoginSecurityStep = (value: string): value is FirstLoginSecurityStep =>
  value === 'biometrics' || value === 'password' || value === 'completed';

interface BiometricRecommendationContext {
  isPINSetup: boolean;
  biometryType: BiometryType;
  biometricsEnabled: boolean;
}

export const shouldSkipBiometricRecommendation = ({
  isPINSetup,
  biometryType,
  biometricsEnabled,
}: BiometricRecommendationContext): boolean =>
  !isPINSetup || !biometryType || biometricsEnabled;

/**
 * Read the persisted one-time security recommendation step.
 * Invalid or missing values are treated as an uninitialized flow.
 */
export const getFirstLoginSecurityStep = async (): Promise<FirstLoginSecurityStep | null> => {
  try {
    const credentials = await Keychain.getGenericPassword({
      service: FIRST_LOGIN_SECURITY_SERVICE,
    });

    if (!credentials || !isFirstLoginSecurityStep(credentials.password)) {
      return null;
    }

    return credentials.password;
  } catch (error) {
    console.error('Error reading first-login security step:', error);
    return null;
  }
};

/** Persist progress without tying it to a specific account. */
export const setFirstLoginSecurityStep = async (
  step: FirstLoginSecurityStep,
): Promise<boolean> => {
  try {
    await Keychain.setGenericPassword('step', step, {
      service: FIRST_LOGIN_SECURITY_SERVICE,
    });
    return true;
  } catch (error) {
    console.error('Error saving first-login security step:', error);
    return false;
  }
};

/**
 * Initialize the flow once per device.
 *
 * Returning users are migrated directly to `completed`, so installing the
 * feature does not show first-login recommendations retroactively. A genuinely
 * new user starts at the biometric recommendation and can resume from the
 * stored step if the app closes before the flow is finished.
 */
export const initializeFirstLoginSecurityStep = async (
  hasLoggedInBefore: boolean,
): Promise<FirstLoginSecurityStep> => {
  const storedStep = await getFirstLoginSecurityStep();
  if (storedStep) {
    return storedStep;
  }

  const initialStep: FirstLoginSecurityStep = hasLoggedInBefore
    ? 'completed'
    : 'biometrics';
  await setFirstLoginSecurityStep(initialStep);
  return initialStep;
};
